/**
 * reload-button.tsx — Nút "Tải lại" của MỘT thẻ (chỉ tải lại dữ liệu của thẻ đó — danh sách kho, nguồn sản phẩm, kênh,
 * trạng thái…). Dùng cạnh các nút mở màn khác ở tab mới: làm xong bên tab kia, quay lại bấm Tải lại (các query đó cũng
 * tự làm mới khi quay lại tab — `refetchOnWindowFocus`).
 *
 * Props: `onReload` (hàm trả Promise — vd `refetch`) và/hoặc `queryKeys` (invalidate → tải lại các query đang hiển thị).
 * Đang tải: icon xoay; xong: dòng nhỏ "Đã cập nhật" vài giây (lỗi: "Chưa tải lại được").
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { reloadFailed } from './open-target-lib'

type State = 'idle' | 'loading' | 'done' | 'error'

const FEEDBACK_MS = 3_000

export function ReloadButton({
  onReload, queryKeys, what, label: labelProp, iconOnly = false, variant = 'ghost', size = 'sm', className, disabled,
}: {
  onReload?: () => Promise<unknown> | void
  queryKeys?: QueryKey[]
  /** Thứ được tải lại, cho aria-label / title: "danh sách kho" → "Tải lại danh sách kho". */
  what?: string
  label?: string
  iconOnly?: boolean
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
  disabled?: boolean
}) {
  const { t } = useTranslation('aiComp')
  const label = labelProp ?? t('reload.label')
  const qc = useQueryClient()
  const [state, setState] = useState<State>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    // Đặt lại true khi gắn (StrictMode gắn → gỡ → gắn lại trong môi trường dev).
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  async function run() {
    if (state === 'loading') return
    if (timer.current) clearTimeout(timer.current)
    setState('loading')
    let next: State = 'done'
    try {
      const results = await Promise.all([
        onReload?.(),
        ...(queryKeys ?? []).map((queryKey) => qc.invalidateQueries({ queryKey }, { throwOnError: true })),
      ])
      if (reloadFailed(results)) next = 'error'
    } catch {
      next = 'error'
    }
    if (!alive.current) return
    setState(next)
    timer.current = setTimeout(() => setState('idle'), FEEDBACK_MS)
  }

  const aria = what ? t('reload.aria', { label, what }) : label
  const busy = state === 'loading'
  return (
    <span className="inline-flex items-center gap-1.5">
      <Button
        type="button" variant={variant} size={iconOnly ? 'icon' : size}
        className={cn(iconOnly && 'h-7 w-7', className)}
        onClick={() => void run()} disabled={disabled || busy}
        aria-label={aria} title={aria} aria-busy={busy}
      >
        <RefreshCw className={cn('h-3.5 w-3.5', busy && 'animate-spin')} aria-hidden="true" />
        {!iconOnly && label}
      </Button>
      <span aria-live="polite" className={cn('text-[11px]', state === 'error' ? 'text-destructive' : 'text-success', state !== 'done' && state !== 'error' && 'sr-only')}>
        {state === 'done' ? t('reload.done') : state === 'error' ? t('reload.failed') : ''}
      </span>
    </span>
  )
}
